import { useState } from 'react';
import { Button, Caption } from '@/ui';
import { computeVerdict, type MisereVerdict } from './misereVerdict';

export type { MisereVerdict } from './misereVerdict';

/**
 * MisereDecision — composant de jugement binaire pour le mode
 * `tactic_drill_misere` (MVP2.6 Gap 3).
 *
 * Objectif pédagogique (cf. PRODUCT.md §4.2) : Léon doit décider entre
 *   - « Il y a une tactique » (et il doit la jouer)
 *   - « Non, calme jeu » (rien à chercher, position banale)
 *
 * Force le calcul de la réfutation : si Léon clique tactique sur une
 * position banale, on lui montre comment l'adversaire réfute son coup
 * "tactique" raté. C'est pédagogiquement utile pour combattre la sur-
 * confiance, qui est une cause #1 hypothétique du gap puzzle/blitz.
 *
 * V0 (cette implémentation) :
 *  - Composant pur de décision. Reçoit `hasTactic` (côté parent qui
 *    a tiré au sort 50/50 ou marqué `is_lure` dans le YAML).
 *  - 2 boutons cliquables. Au clic, calcule le verdict :
 *    - hasTactic=true + clicTactic  → onDecide('correct_positive')
 *    - hasTactic=true + clicCalme   → onDecide('false_negative')
 *    - hasTactic=false + clicTactic → onDecide('false_positive')
 *    - hasTactic=false + clicCalme  → onDecide('correct_negative')
 *  - Si correct_positive : le parent enclenche le drill normal.
 *  - Si false_positive   : le parent affiche la "réfutation" qui aurait
 *    sanctionné un coup tactique raté (passé en `refutationSan`).
 *
 * V1 (hors scope) :
 *  - Pool dédié `is_lure: true` dans `data/tactics/<theme>/*.yaml`.
 *  - Génération automatique de leurres via Stockfish (positions où la
 *    meilleure « apparente » perd).
 */

export interface MisereDecisionProps {
  /**
   * Si `true` : la position contient une vraie tactique gagnante. Si `false` :
   * c'est un leurre (calme jeu). Le parent gère le tirage 50/50 ou le flag
   * `is_lure` du puzzle.
   */
  hasTactic: boolean;
  /**
   * Callback déclenché AU PREMIER clic. Le composant verrouille sa décision
   * ensuite (pas de re-clic). Le parent décide quoi faire en fonction du
   * verdict.
   */
  onDecide: (verdict: MisereVerdict) => void;
  /**
   * Optionnel — état de décision déjà prise (pour ré-rendre l'UI verrouillée
   * après une refresh). Si fourni, l'UI montre directement le résultat sans
   * permettre de re-cliquer.
   */
  initialVerdict?: MisereVerdict;
}

const VERDICT_LABEL_FR: Record<MisereVerdict, string> = {
  correct_positive: 'Bien vu — il y avait une tactique. Joue-la.',
  correct_negative: 'Exact — pas de tactique forte ici. Calme jeu.',
  false_positive: 'Trop optimiste — pas de tactique. Voici la réfutation.',
  false_negative: 'Tactique manquée — il y avait pourtant un coup gagnant.',
};

const VERDICT_TONE: Record<MisereVerdict, 'volt' | 'coral'> = {
  correct_positive: 'volt',
  correct_negative: 'volt',
  false_positive: 'coral',
  false_negative: 'coral',
};

export function MisereDecision({
  hasTactic,
  onDecide,
  initialVerdict,
}: MisereDecisionProps): JSX.Element {
  const [verdict, setVerdict] = useState<MisereVerdict | null>(initialVerdict ?? null);

  function handleChoice(userSaysTactic: boolean): void {
    if (verdict !== null) return; // verrouillé
    const v = computeVerdict(hasTactic, userSaysTactic);
    setVerdict(v);
    onDecide(v);
  }

  if (verdict !== null) {
    const tone = VERDICT_TONE[verdict];
    const label = VERDICT_LABEL_FR[verdict];
    return (
      <section
        data-testid="misere-decision"
        data-state="resolved"
        data-verdict={verdict}
        className={`flex flex-col gap-2 p-4 rounded-lg glass ${tone === 'volt' ? 'glass-volt' : 'glass-coral'}`}
      >
        <Caption color={tone}>Jugement</Caption>
        <p className="text-body-sm text-paper-50 leading-relaxed">{label}</p>
      </section>
    );
  }

  return (
    <section
      data-testid="misere-decision"
      data-state="pending"
      className="flex flex-col gap-3 p-4 rounded-lg glass glass-cyan"
    >
      <header className="flex flex-col gap-1">
        <Caption color="cyan">Misère</Caption>
        <p className="text-body-sm text-paper-50 leading-relaxed">
          Y a-t-il une tactique dans cette position ? Décide avant de jouer.
        </p>
      </header>
      <div className="flex items-center gap-3 flex-wrap">
        <span data-testid="misere-yes">
          <Button
            variant="primary"
            accent="cyan"
            onClick={() => handleChoice(true)}
          >
            Il y a une tactique
          </Button>
        </span>
        <span data-testid="misere-no">
          <Button variant="secondary" onClick={() => handleChoice(false)}>
            Non, calme jeu
          </Button>
        </span>
      </div>
    </section>
  );
}
